import React, { useState } from 'react';

import { CloseOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Divider, Input, InputRef, message, Popconfirm, Row, Select, SelectProps, Space, theme } from 'antd';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import styled, { createGlobalStyle } from 'styled-components';

import { NSKeys } from '@app/i18n';
import Api from '@app/services/api';

export const USER_TAGS_QUERY_KEY = ['userTags'];

interface IProps extends Omit<SelectProps<string[]>, 'mode' | 'options'> {
  removable?: boolean;
}

const { Option } = Select;

const POPUP_CLASS_NAME = 'user-tags-select-popup';

const SelectPopupStyles = createGlobalStyle`
  .${POPUP_CLASS_NAME} .ant-select-item-option-content {
    min-width: 0;
  }

  .${POPUP_CLASS_NAME} .ant-select-item-option-state {
    margin-left: 8px;
    margin-right: 24px;
  }
`;

const OptionContent = styled(Row)`
  width: 100%;
  min-width: 0;
  gap: 8px;
`;

const TagTitle = styled.span`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const DeleteIconButton = styled.span`
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
`;

export const UserTagsSelect: React.FC<IProps> = ({ removable, value, onChange, ...props }) => {
  const [newTagTitle, setNewTagTitle] = useState('');
  const inputRef = React.useRef<InputRef>(null);
  const commonT = useTranslation(NSKeys.common);
  const assignmentsT = useTranslation(NSKeys.assignments);
  const validationT = useTranslation(NSKeys.validation);
  const { token } = theme.useToken();
  const queryClient = useQueryClient();

  const { data: tags = [], isLoading } = useQuery({
    queryKey: USER_TAGS_QUERY_KEY,
    queryFn: () => Api.users.getDictionaryItems('tags'),
    staleTime: 5 * 60 * 1000,
  });

  const { mutate: createTag, isLoading: isCreateLoading } = useMutation({
    mutationFn: (title: string) => Api.users.postDictionaryItem('tags', title),
    onSuccess: async (created) => {
      onChange?.([...new Set([...(value || []), created.uid])]);
      setNewTagTitle('');
      await queryClient.invalidateQueries(USER_TAGS_QUERY_KEY);
      inputRef.current?.focus();
    },
    onError: (error: unknown) => {
      const response = (error as AxiosError<{ code?: string; errors?: { code?: string }[] }>).response?.data;
      const code = response?.errors?.[0]?.code || response?.code;

      message.error(
        validationT.t(code as never, {
          defaultValue: commonT.t('error400'),
        })
      );
    },
  });

  const { mutate: deleteTag, isLoading: isDeleteLoading } = useMutation({
    mutationFn: (uid: string) => Api.users.deleteDictionaryItem(uid),
    onSuccess: async (_, uid) => {
      onChange?.((value || []).filter((selectedUid) => selectedUid !== uid));
      await queryClient.invalidateQueries(USER_TAGS_QUERY_KEY);
    },
    onError: () => {
      message.error(commonT.t('error400'));
    },
  });

  const handleCreateTag = () => {
    const title = newTagTitle.trim();
    if (!title) return;

    createTag(title);
  };

  return (
    <>
      <SelectPopupStyles />
      <Select
        {...props}
        mode="multiple"
        allowClear
        value={value}
        onChange={onChange}
        loading={isLoading || isDeleteLoading}
        optionLabelProp="label"
        optionFilterProp="label"
        popupClassName={POPUP_CLASS_NAME}
        popupRender={(menu) => (
          <div>
            {menu}
            <Divider style={{ margin: '8px 0' }} />
            <Space style={{ padding: '0 8px 4px' }}>
              <Input
                placeholder={assignmentsT.t('tags.addPlaceholder')}
                ref={inputRef}
                value={newTagTitle}
                maxLength={100}
                onChange={(event) => setNewTagTitle(event.target.value)}
                onKeyDown={(event) => event.stopPropagation()}
              />
              <Button
                disabled={!newTagTitle.trim()}
                type="text"
                icon={<PlusOutlined />}
                loading={isCreateLoading}
                onClick={(event) => {
                  event.preventDefault();
                  handleCreateTag();
                }}>
                {assignmentsT.t('tags.addButton')}
              </Button>
            </Space>
          </div>
        )}>
        {tags.map((tag) => (
          <Option key={tag.uid} label={tag.title} value={tag.uid}>
            <OptionContent justify="space-between" align="middle">
              <TagTitle>{tag.title}</TagTitle>
              {removable && tag?.$permissions?.delete && (
                <Popconfirm
                  title={commonT.t('deleteConfirm')}
                  okText={commonT.t('yes')}
                  onConfirm={(event) => {
                    event?.stopPropagation();
                    deleteTag(tag.uid);
                  }}>
                  <DeleteIconButton onClick={(event) => event.stopPropagation()}>
                    <CloseOutlined style={{ color: token.colorTextDescription }} />
                  </DeleteIconButton>
                </Popconfirm>
              )}
            </OptionContent>
          </Option>
        ))}
      </Select>
    </>
  );
};
